import { useEffect, useState } from "react";
import { AuditTable } from "../../components/AuditTable";
import { Shell } from "../../components/Shell";
import { Button, ErrorNotice, Field, Loading, PageHead, Panel, Select, StatusBadge, EnvBadge } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { useQuery } from "../../lib/query";
import { useSearchParam } from "../../lib/router";
import { useClient } from "../../lib/session";
import { formatDateTime } from "../../lib/time";
import type { AuditLog, ReconReport } from "../../lib/types";

const ACTIONS = [
  ["", "Everything"],
  ["developer", "Developers and credentials"],
  ["game.", "Games"],
  ["api_key.", "API keys"],
  ["bank.", "Banks"],
  ["limits.", "Limits"],
  ["clock.", "Game clocks"],
  ["funding.", "Funding"],
  ["transfer.", "Transfers"],
  ["hold.", "Holds"],
  ["transaction.", "Reversals"],
  ["account.", "Accounts"],
  ["reconciliation.", "Reconciliation"],
];

export function AdminAudit() {
  const api = adminApi(useClient());
  const [action, setAction] = useSearchParam("action");
  const [envF, setEnvF] = useSearchParam("environment");
  const [devId, setDevId] = useSearchParam("developer_id");
  const [gameId, setGameId] = useSearchParam("game_id");
  const [older, setOlder] = useState<AuditLog[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const filter = { action, environment: envF, developer_id: devId, game_id: gameId };
  const key = `admin:audit:list:${action}:${envF}:${devId}:${gameId}`;
  const q = useQuery(key, () => api.auditLogs({ ...filter, limit: 50 }));
  useEffect(() => setOlder([]), [key]);
  const rows = [...(q.data?.data ?? []), ...older];
  const canMore = (q.data?.data.length ?? 0) === 50 && (older.length === 0 || older.length % 50 === 0);

  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const r = await api.auditLogs({ ...filter, limit: 50, before: last.created_at });
      setOlder((o) => [...o, ...r.data]);
    } catch (e) {
      setMoreError(e);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Shell crumbs={[{ label: "Audit log" }]}>
      <PageHead title="Audit log" lede="Every sensitive action on the engine, newest first. Entries are append-only and can't be edited or deleted." />
      <Panel
        flush
        actions={
          <div className="row end">
            <Field label="Show">
              <Select value={action} onChange={(e) => setAction(e.target.value)}>
                {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Environment">
              <Select value={envF} onChange={(e) => setEnvF(e.target.value)}>
                <option value="">Both</option>
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </Select>
            </Field>
          </div>
        }
        title={
          devId || gameId ? (
            <span className="row">
              Filtered to one {devId ? "developer" : "game"}
              <Button size="small" onClick={() => { setDevId(""); setGameId(""); }}>Clear</Button>
            </span>
          ) : "All activity"
        }
      >
        <ErrorNotice error={q.error} onRetry={q.reload} />
        {q.loading && !q.data && <Loading />}
        {q.data && <AuditTable rows={rows} />}
        <ErrorNotice error={moreError} />
        {canMore && (
          <div className="pager">
            <Button size="small" busy={loadingMore} onClick={loadMore}>Load older entries</Button>
          </div>
        )}
      </Panel>
    </Shell>
  );
}

export function AdminIntegrity() {
  const api = adminApi(useClient());
  const [env, setEnv] = useState("");
  const [report, setReport] = useState<ReconReport | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [state, setState] = useSearchParam("state");
  const jobs = useQuery(`admin:jobs:${state}`, () => api.jobs({ state, limit: 50 }));

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      setReport(await api.reconcile({ environment: env }));
    } catch (e) {
      setError(e);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Shell crumbs={[{ label: "Ledger health" }]}>
      <PageHead
        title="Ledger health"
        lede="Reconciliation recomputes every balance from the immutable ledger and compares it with the stored balance. It also checks that every bank nets to zero and that every transaction balances."
      />
      <div className="stack">
        <Panel
          title="Reconciliation"
          actions={
            <div className="row end">
              <Field label="Scope">
                <Select value={env} onChange={(e) => setEnv(e.target.value)}>
                  <option value="">Whole platform</option>
                  <option value="sandbox">Sandbox only</option>
                  <option value="production">Production only</option>
                </Select>
              </Field>
              <Button variant="primary" busy={running} onClick={run}>Run reconciliation</Button>
            </div>
          }
        >
          <ErrorNotice error={error} />
          {!report && !error && <p className="muted">Runs read-only. It's safe to run at any time, including in production.</p>}
          {report && (
            <div className="stack" style={{ gap: 14 }}>
              <div className={`notice ${report.ok ? "role" : "error"}`} role="status">
                <strong>{report.ok ? "The books balance." : `${report.differences.length} difference${report.differences.length === 1 ? "" : "s"} found.`}</strong>{" "}
                Checked {report.accounts_checked.toLocaleString()} accounts and {report.transactions_checked.toLocaleString()} transactions
                in {Math.max(1, new Date(report.finished_at).getTime() - new Date(report.started_at).getTime())} ms.
              </div>
              {!report.ok && (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr><th>Problem</th><th>Where</th><th className="num">Stored</th><th className="num">Ledger says</th></tr>
                    </thead>
                    <tbody>
                      {report.differences.map((d, i) => (
                        <tr key={i}>
                          <td>{d.kind.replace(/_/g, " ")}</td>
                          <td className="mono">{d.account_number ?? d.transaction_id ?? d.bank_id}<div className="cell-sub">{d.environment}</div></td>
                          <td className="num">{d.stored}</td>
                          <td className="num">{d.expected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Scheduled jobs"
          sub="Daily jobs run once per game day for each game and environment."
          flush
          actions={
            <Select value={state} onChange={(e) => setState(e.target.value)} aria-label="Filter jobs">
              <option value="">All runs</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
            </Select>
          }
        >
          <ErrorNotice error={jobs.error} onRetry={jobs.reload} />
          {jobs.data && jobs.data.data.length === 0 && <p className="muted panel-body">No job runs yet. They start once a game has a bank.</p>}
          {jobs.data && jobs.data.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Job</th><th>Game</th><th>Game day</th><th>Result</th><th>State</th><th>Started</th></tr>
                </thead>
                <tbody>
                  {jobs.data.data.map((j) => (
                    <tr key={j.id}>
                      <td>{j.job.replace(/_/g, " ")}</td>
                      <td>{j.game_name} <EnvBadge env={j.environment} /></td>
                      <td>{j.game_day}</td>
                      <td className="small">
                        {j.job === "reconciliation" && j.result.ok !== undefined
                          ? j.result.ok ? `Clean (${j.result.accounts_checked} accounts)` : `${j.result.differences} differences`
                          : j.result.error ? String(j.result.error) : j.result.pruned_usage_rows !== undefined ? `Pruned ${j.result.pruned_usage_rows} usage rows` : "—"}
                      </td>
                      <td><StatusBadge status={j.state} /></td>
                      <td>{formatDateTime(j.started_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </Shell>
  );
}
